/**
 * Datasets-tab composer for `CollectionDetailPage`.
 *
 * Drives the layout per `feature-spec.md` §2.3: golden-dataset info banner
 * → collection-wide summary strip → grid of `ChannelDatasetCard`s. For
 * draft collections the cards are replaced by `EmptyDatasetsState`.
 *
 * Owns the single-card-expanded state — only one channel card is open at
 * a time within this tab.
 */

import React, { useState } from 'react';

import { Button } from '../../../../../ui/button';
import { useChannelDatasets } from '../../hooks/useDatasets';
import type { ChannelDataset, Collection } from '../../types';
import ChannelDatasetCard from './ChannelDatasetCard';
import CollectionSummaryStrip, {
  type CollectionSummaryStats,
} from './CollectionSummaryStrip';
import EmptyDatasetsState from './EmptyDatasetsState';
import GoldenDatasetInfoBanner from './GoldenDatasetInfoBanner';

interface Props {
  collection: Collection;
  /**
   * Callback the page lifts down so child surfaces (the empty-state
   * "Upload Dataset" button) can switch the user to the Upload tab.
   * Defaults to a no-op for tests / Storybook.
   */
  onSwitchTab?: (tab: 'datasets' | 'split' | 'upload') => void;
}

const aggregate = (datasets: ChannelDataset[]): CollectionSummaryStats => {
  const totalRecords = datasets.reduce((s, d) => s + d.stats.totalRecords, 0);
  const annotated = datasets.reduce((s, d) => s + d.stats.annotated, 0);
  const finalised = datasets.reduce((s, d) => s + d.stats.finalised, 0);
  const targetSum = datasets.reduce((s, d) => s + d.target, 0);
  return {
    totalRecords,
    golden: finalised,
    inProgress: annotated - finalised,
    awaiting: Math.max(0, totalRecords - annotated),
    targetSum,
    progressPct:
      targetSum > 0
        ? Math.min(100, Math.round((finalised / targetSum) * 100))
        : 0,
  };
};

const DatasetsTabContent: React.FC<Props> = ({ collection, onSwitchTab }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useChannelDatasets(collection.id);

  if (collection.status !== 'active') {
    return <EmptyDatasetsState onUploadDataset={() => onSwitchTab?.('upload')} />;
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="channel-datasets-loading">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-32 animate-pulse rounded-lg border border-border/70 bg-card"
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-[40vh] flex-col items-center justify-center text-center">
        <p className="mb-4 text-muted-foreground">
          Failed to load channel datasets.
        </p>
        <Button variant="link" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const datasets = data ?? [];
  const summary = aggregate(datasets);

  return (
    <div className="space-y-6" data-testid="datasets-tab-content">
      <GoldenDatasetInfoBanner
        golden={summary.golden}
        totalRecords={summary.totalRecords}
      />
      <CollectionSummaryStrip summary={summary} tabKey="datasets" />
      <div className="space-y-3">
        {datasets.map((ds) => (
          <ChannelDatasetCard
            key={ds.id}
            dataset={ds}
            taxonomyKey={collection.taxonomyKey}
            taxonomyVersionId={collection.taxonomyVersionId}
            isExpanded={ds.id === expandedId}
            onToggle={() =>
              setExpandedId((prev) => (prev === ds.id ? null : ds.id))
            }
          />
        ))}
        {datasets.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No channel datasets configured yet.
          </p>
        )}
      </div>
    </div>
  );
};

export default DatasetsTabContent;
